import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { paddleDataSuccess, URLs } from "../utils/apis";
import { formatHexStr } from "../utils/formatHexStr";
import {
  Accordion,
  ActionIcon,
  Box,
  CopyButton,
  Flex,
  Image,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import { useStylesButton } from "../utils/mantine/useStylesButton";
import { useStylesStampResult } from "../utils/mantine/useStylesStampResult";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { useStylesLanding } from "../utils/mantine/useStylesLanding";
import { useStylesPagePricing } from "../utils/mantine/useStylesPagePricing";
import { useStylesSubscriptionPage } from "../utils/mantine/useStylesSubscriptionPage";
import { useStylesTechnicalDetail } from "../utils/mantine/useStylesTechnicalDetail";
import { useStylesAccordion } from "../utils/mantine/useStylesAccordion";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";

const defaultInputValues = {
  subscription_info: {
    subscription_paddle_id: false,
    last_transaction_paddle_id: false,
  },
  user_balance_info: {
    expirable_credits: 0,
    paid_credits: 0,
    total_credits: 0,
  },
  subscription_plan: {
    title: false,
    description: false,
    total_quota_limit: 0,
  },
  billing_info: {
    email: "",
  },
};

const SubscriptionSuccess = ({ staticBasePath }) => {
  //css classes
  const { classes: classesPage } = useStylesPage();

  const { classes: classesStampResult } = useStylesStampResult();

  const { classes: buttonClasses } = useStylesButton();

  const { classes: landingClasses } = useStylesLanding();

  const { classes: classesPricingPage } = useStylesPagePricing();

  const { classes: classesSubscriptionPage } = useStylesSubscriptionPage();

  const { classes: classesTechnicalDetail } = useStylesTechnicalDetail();

  const { classes: classesAccordion } = useStylesAccordion();

  const { classes: classesIcons } = useStylesIcons();

  const [inputValues, setInputValues] = useState(defaultInputValues);
  const [isLoading, setIsLoading] = useState(true); // Loading state

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  useEffect(() => {
    paddleDataSuccess(setInputValues, setIsLoading);
  }, []);

  if (isLoading) {
    return (
      <Flex
        pt={theme.padding.xxxloEm}
        justify={theme.flex.justify.center}
        align={theme.flex.align.center}
      >
        Loading...
      </Flex>
    ); // Show a loading message while data is being fetched
  }

  return (
    <Box
      className={`${classesStampResult.stamp} ${classesPage.page} subscription-success`}
    >
      <Flex
        className={classesPage.screen}
        direction="column"
        align={theme.flex.align.center}
      >
        <Box className={classesPage.header}>
          <Image
            src={`${staticBasePath}public/images/svg/vb-icon-verified.svg`}
            alt="Congratulations!"
            className={classesPage.pageHeaderIcon}
          />

          <Title mb={theme.margin.xsEm} order={1}>
            Congratulations!
          </Title>

          <Text className={classesSubscriptionPage.headerText}>
            Your payment has been successfully confirmed.
          </Text>
          <Text className={classesSubscriptionPage.headerText}>
            This receipt has been e-mailed to{" "}
            <Text component="b">{inputValues.billing_info.email}</Text> e-mail
          </Text>

          <Flex
            justify={theme.flex.justify.center}
            gap={theme.flex.gap.mdPlus}
            align={theme.flex.align.center}
            className={`${landingClasses.pricingBlockButtons} ${classesPricingPage.pricingCustomButtons}`}
          >
            <CustomAnchor
              text="Profile"
              href={URLs.USER_BILLING_URL}
              componentClasses={`${buttonClasses.buttonBlack} ${landingClasses.pricingBlockButtonsButton}`}
            />
            <CustomAnchor
              text="Stamp"
              href={URLs.STAMP_URL}
              componentClasses={`${buttonClasses.buttonWhite} ${landingClasses.pricingBlockButtonsButton}`}
            />
          </Flex>
        </Box>

        <Box
          className={`${classesTechnicalDetail.technicalBlock} ${classesTechnicalDetail.technical}`}
        >
          <Accordion
            className={classesAccordion.accordion}
            value="tech"
            variant="contained"
          >
            <Accordion.Item value="tech">
              <Accordion.Panel>
                <Box pb={theme.padding.baseEm}>
                  <Text className={classesSubscriptionPage.technicalTitle}>
                    Subscription details
                  </Text>
                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item"
                    align={theme.flex.align.end}
                  >
                    <Text className="key">Subscription Name</Text>
                    <Box className="dots"></Box>
                    <Text className="value" id="stamp-found__account_id">
                      {inputValues.subscription_plan.title}
                    </Text>
                  </Flex>

                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item"
                    align={theme.flex.align.end}
                  >
                    <Text className="key">Subscription ID</Text>
                    <Box className="dots"></Box>
                    <Text className="value" id="stamp-found__account_id">
                      {formatHexStr(
                        inputValues.subscription_info.subscription_paddle_id,
                      )}

                      <CopyButton
                        value={
                          inputValues.subscription_info.subscription_paddle_id
                        }
                        timeout={2000}
                      >
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied" : "Copy"}
                            className={classesIcons.copy}
                            withArrow
                            position="right"
                          >
                            <ActionIcon
                              color={
                                copied
                                  ? theme.customColors.actionIconFirst
                                  : theme.customColors.actionIconSecond
                              }
                              variant="subtle"
                              onClick={copy}
                            >
                              {copied ? (
                                <IconCheck className={classesIcons.width} />
                              ) : (
                                <IconCopy className={classesIcons.width} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Text>
                  </Flex>

                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item"
                    align={theme.flex.align.end}
                  >
                    <Text className="key">Transaction ID</Text>
                    <Box className="dots"></Box>
                    <Text className="value" id="stamp-found__account_id">
                      {inputValues.subscription_info.last_transaction_paddle_id}

                      <CopyButton
                        value={
                          inputValues.subscription_info
                            .last_transaction_paddle_id
                        }
                        timeout={2000}
                      >
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied" : "Copy"}
                            className={classesIcons.copy}
                            withArrow
                            position="right"
                          >
                            <ActionIcon
                              color={
                                copied
                                  ? theme.customColors.actionIconFirst
                                  : theme.customColors.actionIconSecond
                              }
                              variant="subtle"
                              onClick={copy}
                            >
                              {copied ? (
                                <IconCheck className={classesIcons.width} />
                              ) : (
                                <IconCopy className={classesIcons.width} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Text>
                  </Flex>

                  <Text className={classesSubscriptionPage.technicalTitle}>
                    Your balance
                  </Text>

                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item"
                    align={theme.flex.align.end}
                  >
                    <Text className="key">Newly purchased credits:</Text>
                    <Box className="dots"></Box>
                    <Text className="value" id="stamp-found__account_id">
                      {inputValues.subscription_plan.total_quota_limit -
                        inputValues.user_balance_info.expirable_credits}
                    </Text>
                  </Flex>

                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item"
                    align={theme.flex.align.end}
                  >
                    <Text className="key">Previously Purchased Credits:</Text>
                    <Box className="dots"></Box>
                    <Text className="value" id="stamp-found__account_id">
                      {inputValues.user_balance_info.paid_credits -
                        inputValues.subscription_plan.total_quota_limit +
                        inputValues.user_balance_info.expirable_credits}
                    </Text>
                  </Flex>

                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item"
                    align={theme.flex.align.end}
                  >
                    <Text className="key">Monthly Free Credits Remaining:</Text>
                    <Box className="dots"></Box>
                    <Text className="value" id="stamp-found__account_id">
                      {inputValues.user_balance_info.expirable_credits}
                    </Text>
                  </Flex>

                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item"
                    align={theme.flex.align.end}
                  >
                    <Text className="key">Total balance:</Text>
                    <Box className="dots"></Box>
                    <Text className="value" id="stamp-found__account_id">
                      {inputValues.user_balance_info.total_credits}
                    </Text>
                  </Flex>
                </Box>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Box>
      </Flex>
    </Box>
  );
};

SubscriptionSuccess.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
};
export default SubscriptionSuccess;
